import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // API 기본 URL 설정
  const getApiUrl = () => {
    if (process.env.NODE_ENV === 'production') {
      // 배포 환경: 백엔드 서버 포트 8080 사용
      return 'https://test.missing-person.app.noaplc.kr:8080';
    }
    // 개발 환경: 빈 문자열 (proxy 사용)
    return '';
  };

  // axios 인터셉터 설정
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    // 응답 인터셉터로 401 에러 처리
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          logout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // 앱 시작 시 토큰 검증
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const apiUrl = getApiUrl();
      const response = await axios.get(`${apiUrl}/api/auth/verify`);
      setUser(response.data.user);
    } catch (error) {
      console.error('Token verification failed:', error);
      // 토큰이 유효하지 않거나 만료된 경우 조용히 처리
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const apiUrl = getApiUrl();
      console.log('Login URL:', `${apiUrl}/api/auth/login`);

      const response = await axios.post(`${apiUrl}/api/auth/login`, {
        username,
        password
      });

      const { token, user } = response.data;
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);

      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || '로그인에 실패했습니다'
      };
    }
  };

  const register = async (username, password, inviteCode) => {
    try {
      const apiUrl = getApiUrl();
      console.log('Register URL:', `${apiUrl}/api/auth/register`);
      console.log('Environment:', process.env.NODE_ENV);

      const response = await axios.post(`${apiUrl}/api/auth/register`, {
        username,
        password,
        inviteCode
      });

      const { token, user } = response.data;
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);

      return { success: true };
    } catch (error) {
      console.error('Registration failed:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method
      });

      return {
        success: false,
        error: error.response?.data?.error || '회원가입에 실패했습니다'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};